import {
    IVanillaReaderBookmarkData,
    IVanillaReaderContentPositionData,
    IVanillaReaderHighlightData,
    IVanillaReaderPublicationCacheData,
    IVanillaReaderPublicationData,
    IVanillaReaderPublicationOptionsData,
} from "../VanillaReader/VanillaReaderModel";
import {IPublicationMetadataData} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import {VanillaReaderAppStorage} from "../VanillaReaderApp/VanillaReaderAppStorage";
import {VanillaReaderShelfItemStoredResource} from "./VanillaReaderShelfItemStoredResource";
import {IStoredResource,} from "../VanillaReaderDataStore/VanillaReaderStreamedResourceIndexDbStorage";

/*
* # VanillaReaderShelfItem
*
* ## RESPONSIBILITIES
*
* Shelf items manages the state of publications, such as metadata, reading position, bookmarks and highlights etc.
* Think of the shelf item as what actual represents the "used" book, with all the notes, bookmarks, dog eared pages etc.
*
* New instances of this class is created by calling the `VanillaReaderBookshelf.addShelfItem()` method.
*
* Anytime a book is opened for the first time by the app, a new instance of a `VanillaReaderShelfItem` is created with
* the bare minimum of data: id, file name, file uri (the path to the book), and format. This instance is then sent
* along in one of the `loadPublication*` events to the `VanillaReader` to be used for the actual loading of the
* publication.
*
* ## RELATED TYPES
*
* - VanillaReaderAppStorage
* - VanillaReaderBookshelf
* - VanillaReaderPublication
*
* */
export class VanillaReaderShelfItem implements IVanillaReaderPublicationData {
    private _id: string;
    private readonly _fileName: string;
    private readonly _fileSourceUri: string;
    private readonly _url: string;
    private readonly _format: string;
    private readonly _latestReadingPositionData?: IVanillaReaderContentPositionData | undefined;
    private _starred?: boolean | undefined;
    private _categories?: string[];
    private _title?: string | undefined;
    private _isRtl?: boolean | undefined;
    private _isFixedLayout?: boolean | undefined;
    private _isScripted?: boolean | undefined;
    private _isAudiobook?: boolean | undefined;
    private _hasMoSyncMedia?: boolean | undefined;
    private _metadata?: IPublicationMetadataData | undefined;
    private _coverImageAsBase64?: string | undefined;
    private _language?: string | undefined;
    private _publicationData: IVanillaReaderPublicationData | undefined;

    private _hasBeenPersisted: boolean = false;

    private _storedResource: VanillaReaderShelfItemStoredResource | undefined;

    private constructor(private _vanillaReaderAppStorage: VanillaReaderAppStorage, data: IVanillaReaderPublicationData, hasBeenPersisted: boolean, latestReadingPositionData?: IVanillaReaderContentPositionData, storedResource?: VanillaReaderShelfItemStoredResource) {
        this._id = data.id;
        this._fileName = data.fileName;
        this._fileSourceUri = data.fileSourceUri;
        this._url = data.url;
        this._format = data.format;
        this._language = data.language;
        this._isFixedLayout = data.isFixedLayout;
        this._title = data.title;
        this._isRtl = data.isRtl;
        this._isScripted = data.isScripted;
        this._isAudiobook = data.isAudiobook;
        this._hasMoSyncMedia = data.hasMoSyncMedia;
        this._metadata = data.metadata;
        this._coverImageAsBase64 = data.coverImageAsBase64;
        this._latestReadingPositionData = latestReadingPositionData;

        this.hasBeenPersisted = hasBeenPersisted;

        if(storedResource) {
            this._storedResource = storedResource;
        }
    }

    static async instantiate(stateData: IVanillaReaderPublicationData, vanillaReaderAppStorage: VanillaReaderAppStorage): Promise<VanillaReaderShelfItem> {
        let latestReadingPosition: IVanillaReaderContentPositionData | undefined;

        let exists = await vanillaReaderAppStorage.publicationDataStore.hasPublicationData(stateData.id);

        if(!exists) {
            throw new DOMException(`VanillaReaderShelfItem.instantiate, item does not exist "${stateData.fileName}".`);
        }

        latestReadingPosition = await vanillaReaderAppStorage.publicationDataStore.fetchPublicationReadingPositionData(stateData.id) || undefined;

        return  new VanillaReaderShelfItem(vanillaReaderAppStorage, stateData, true, latestReadingPosition);
    }

    static async create(fileName: string, fileSourceUri: string, format: string, vanillaReaderAppStorage: VanillaReaderAppStorage): Promise<VanillaReaderShelfItem> {
        let hasBeenPersisted: boolean = false;
        let data: IVanillaReaderPublicationData | undefined;
        let id: string;
            id = fileSourceUri;

            data = {
                id: fileSourceUri,
                fileName,
                fileSourceUri,
                url: fileSourceUri,
                format,
            };

            await vanillaReaderAppStorage.publicationDataStore.setPublicationData(id, data);


        return new VanillaReaderShelfItem(vanillaReaderAppStorage, data, hasBeenPersisted);
    }

    get id(): string {
        return this._id;
    }

    hasOfflineData(): Promise<boolean> {
        return this._vanillaReaderAppStorage.streamedResourcesDataStore.hasResource(this.fileSourceUri);
    }

    get readingPositionData(): IVanillaReaderContentPositionData | undefined {
        return this._latestReadingPositionData;
    }

    get language(): string | undefined {
        return this._language;
    }

    get coverImageAsBase64(): string | undefined {
        return this._coverImageAsBase64;
    }

    get metadata(): IPublicationMetadataData | undefined {
        return this._metadata;
    }

    get hasMoSyncMedia(): boolean | undefined {
        return this._hasMoSyncMedia;
    }

    get isAudiobook(): boolean | undefined {
        return this._isAudiobook;
    }

    get isScripted(): boolean | undefined {
        return this._isScripted;
    }

    get isFixedLayout(): boolean | undefined {
        return this._isFixedLayout;
    }

    get isRtl(): boolean | undefined {
        return this._isRtl;
    }

    get url(): string {
        return this._url;
    }

    get format(): string {
        return this._format;
    }

    get title(): string | undefined {
        return this._title;
    }

    get fileSourceUri(): string {
        return this._fileSourceUri;
    }

    get fileName(): string {
        return this._fileName;
    }

    get categories(): string[] | undefined {
        return this._categories;
    }

    set categories(values: string[] | undefined) {
        this._categories = values;
    }

    get starred(): boolean | undefined {
        return this._starred;
    }

    set starred(value: boolean | undefined) {
        this._starred = value;
    }

    get hasBeenPersisted(): boolean {
        return this._hasBeenPersisted;
    }

    set hasBeenPersisted(persisted:boolean) {
        this._hasBeenPersisted = persisted;
    }

    get publicationData(): IVanillaReaderPublicationData | undefined {
        return this._publicationData;
    }

    fetchBookmarks(): Promise<IVanillaReaderBookmarkData[]> {
        return this._vanillaReaderAppStorage.bookmarkDataStore.fetchBookmarksByPublication(this.id);
    }

    addBookmark(bookmarkData: IVanillaReaderBookmarkData): Promise<IVanillaReaderBookmarkData> {
        return this._vanillaReaderAppStorage.bookmarkDataStore.addBookmark(bookmarkData);
    }

    deleteBookmark(bookmarkLocator: string): Promise<void> {
       return  this._vanillaReaderAppStorage.bookmarkDataStore.deleteBookmark(bookmarkLocator);
    }

    fetchHighlights(): Promise<IVanillaReaderHighlightData[]> {
        return this._vanillaReaderAppStorage.highlightDataStore.fetchHighlightsByPublication(this.id);
    }

    addHighlights(highlightData: IVanillaReaderHighlightData[], silent: boolean): Promise<void> {
        return this._vanillaReaderAppStorage.highlightDataStore.addHighlights(highlightData, silent);
    }

    addHighlight(highlightData: IVanillaReaderHighlightData): Promise<IVanillaReaderHighlightData> {
        return this._vanillaReaderAppStorage.highlightDataStore.addHighlight(highlightData);
    }

    updateHighlight(highlightData: IVanillaReaderHighlightData): Promise<void> {
        return this._vanillaReaderAppStorage.highlightDataStore.updateHighlight(highlightData);
    }

    deleteHighlight(highlightLocator: string): Promise<void> {
        return this._vanillaReaderAppStorage.highlightDataStore.deleteHighlight(highlightLocator);
    }

    fetchReadingPositionData(): Promise<IVanillaReaderContentPositionData | undefined> {
        //Removed this to prevent previous stored locators in browser from reopening at last-left-off page.
        return Promise.resolve(undefined);
        //return this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationReadingPositionData(this.id);
    }

    setReadingPositionData(readingPositionData: IVanillaReaderContentPositionData): Promise<void> {
        return this._vanillaReaderAppStorage.publicationDataStore.setPublicationReadingPositionData(this._id, readingPositionData);
    }

    deleteStoredResource(): Promise<void> {
        this._storedResource = undefined;
        return this._vanillaReaderAppStorage.streamedResourcesDataStore.deleteResource(this.id);
    }

    hasCachedData(): Promise<boolean> {
        return this._vanillaReaderAppStorage.publicationDataStore.hasPublicationCacheData(this.id);
    }

    fetchCacheData(): Promise<IVanillaReaderPublicationCacheData | undefined> {
        return this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationCacheData(this.id);
    }

    setCacheData(cacheData: IVanillaReaderPublicationCacheData): Promise<void> {
        return this._vanillaReaderAppStorage.publicationDataStore.setPublicationCacheData(this.id, cacheData);
    }

    updateCacheData(cacheData: IVanillaReaderPublicationCacheData): Promise<void> {
        return this._vanillaReaderAppStorage.publicationDataStore.updatePublicationCacheData(this.id, cacheData);
    }

    deleteCacheData(): Promise<number> {
        return this._vanillaReaderAppStorage.publicationDataStore.deletePublicationCacheData(this.id);
    }

    hasOptionsData(): Promise<boolean> {
        return this._vanillaReaderAppStorage.publicationDataStore.hasPublicationOptionsData(this.id);
    }

    fetchOptionsData(): Promise<IVanillaReaderPublicationOptionsData | undefined> {
        return this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationOptionsData(this.id);
    }

    setOptionsData(optionsData: IVanillaReaderPublicationOptionsData): Promise<void> {
        return this._vanillaReaderAppStorage.publicationDataStore.setPublicationOptionsData(this.id, optionsData);
    }

    updateOptionsData(optionsData: IVanillaReaderPublicationOptionsData): Promise<void> {
        return this._vanillaReaderAppStorage.publicationDataStore.updatePublicationOptionsData(this.id, optionsData);
    }

    fetchPublicationData(): Promise<IVanillaReaderPublicationData | undefined> {
        return this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationData(this.id);
    }

    async setPublicationData(data: IVanillaReaderPublicationData): Promise<void> {
        this._id = data.id;
        this._title = data.title;
        this._isRtl = data.isRtl;
        this._isFixedLayout = data.isFixedLayout;
        this._isScripted = data.isScripted;
        this._isAudiobook = data.isAudiobook;
        this._hasMoSyncMedia = data.hasMoSyncMedia;
        this._metadata = data.metadata;
        this._coverImageAsBase64 = data.coverImageAsBase64;
        this._language = data.language;

        return this._vanillaReaderAppStorage.publicationDataStore.updatePublicationDataByFileName(this.fileName, data).then(()=>{
            this.hasBeenPersisted = true;
        });
    }

    async hasStoredResource(): Promise<boolean> {
        return  this._vanillaReaderAppStorage.streamedResourcesDataStore.hasResource(this.fileSourceUri);
    }

    async fetchStoredResource(): Promise<VanillaReaderShelfItemStoredResource | undefined> {

        if(!this._storedResource) {
            let storedResourceData: IStoredResource | undefined = await this._vanillaReaderAppStorage.streamedResourcesDataStore.getResource(this.fileSourceUri);
            if(storedResourceData) {
                this._storedResource = await VanillaReaderShelfItemStoredResource.instantiate(storedResourceData, this._vanillaReaderAppStorage.streamedResourcesDataStore);
            }
        }
        return this._storedResource;
    }

    async createStoredResource(size: number = 0, file?: File): Promise<VanillaReaderShelfItemStoredResource> {

        if(!this.id) {
            throw new DOMException(`VanillaReaderShelfItem.createStoredResource(), Shelf item id is undefined.`);
        }

        /*
        * NOTE
        *
        * If you want to make it harder for hackers to extract publication data from the store, please use some kind of
        * hashing strategy for the parameter data.
        */
        this._storedResource =  await VanillaReaderShelfItemStoredResource.create(this._vanillaReaderAppStorage.streamedResourcesDataStore,this.fileSourceUri, this.fileName, this.url, this.format, size);
        if(file) {
            this._storedResource.size = file.size;
            let fileArrayBuffer = await file.arrayBuffer();
            this._vanillaReaderAppStorage.streamedResourcesDataStore.addResourceData(this.fileSourceUri, 0, file.size, fileArrayBuffer).catch(reason => {
                console.error(reason);
            })
        }

        return this._storedResource;
    }

    async serialize(): Promise<IVanillaReaderPublicationData | undefined> {
        return this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationData(this.id);
    }

}
